import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { da } from "date-fns/locale";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ConversationsList from "@/components/ConversationsList";
import PendingReviews from "@/components/PendingReviews";
import { ConnectOnboarding } from "@/components/ConnectOnboarding";
import BookingsCalendar from "@/components/BookingsCalendar";
import EarningsOverview from "@/components/EarningsOverview";
import StripeBalanceWidget from "@/components/StripeBalanceWidget";
import CancelBookingDialog from "@/components/CancelBookingDialog";
import RenewAvailabilityDialog from "@/components/RenewAvailabilityDialog";
import EditAvailabilityDialog from "@/components/EditAvailabilityDialog";
import { PastBookingCard } from "@/components/PastBookingCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { DollarSign, House, Calendar as CalIcon, Star, Eye, EyeOff, Plus, RefreshCw, Pencil, AlertCircle, CheckCircle2, MessageSquare } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";
import { useToast } from "@/hooks/use-toast";
import { useStripeConnect } from "@/hooks/useStripeConnect";
import AvatarUpload from "@/components/AvatarUpload";
import { expandAvailability } from "@/lib/availability";
import { addDays } from "date-fns";

// Sørg for at denne fil findes (ellers lav den): src/assets/placeholder.svg
import placeholder from "@/assets/placeholder.png?url";

type Listing = {
  id: string;
  title: string;
  address: string;
  visibility: boolean;
  base_price_day: number | null; // øre
  photos: string[] | null;
};

type OwnerBooking = {
  id: string;
  listing_id: string;
  start_at: string;
  end_at: string;
  status: "pending" | "confirmed" | "cancelled" | "completed";
  total_amount: number; // øre
  stripe_payment_intent_id: string | null;
  listings: { id: string; title: string; address: string; cancellation_policy?: string } | null;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { user, loading } = useSession();
  
  // State for renew dialog
  const [renewDialog, setRenewDialog] = useState<{ open: boolean; listingId: string; listingTitle: string }>({
    open: false,
    listingId: "",
    listingTitle: "",
  });

  // State for edit availability dialog
  const [editDialog, setEditDialog] = useState<{ open: boolean; listingId: string; listingTitle: string }>({
    open: false,
    listingId: "",
    listingTitle: "",
  });

  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  // Gate: kræver login
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth?mode=signin&redirect=/dashboard");
    }
  }, [loading, user, navigate]);

  // Check if user has Stripe Connect account
  const stripeConnectQ = useStripeConnect(user?.id);

  // Profil-data til velkomst og avatar
  const profileQ = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("name, email, phone, photo_url")
        .eq("id", user!.id)
        .single();
      if (error) throw error;
      return data as { name: string | null; email: string | null; phone: string | null; photo_url: string | null };
    },
  });

  // Dine pladser
  const listingsQ = useQuery({
    queryKey: ["myListings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("id,title,address,visibility,base_price_day,photos")
        .eq("owner_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Listing[];
    },
  });

  // Hent availability for alle listings
  const availabilityQ = useQuery({
    queryKey: ["listingAvailability", user?.id],
    enabled: !!user && (listingsQ.data?.length ?? 0) > 0,
    queryFn: async () => {
      const listingIds = listingsQ.data?.map(l => l.id) ?? [];
      if (!listingIds.length) return new Map();

      const { data, error } = await supabase
        .from("availability")
        .select("*")
        .in("listing_id", listingIds);

      if (error) throw error;

      // Group by listing_id
      const map = new Map<string, any[]>();
      data?.forEach(av => {
        const existing = map.get(av.listing_id) || [];
        map.set(av.listing_id, [...existing, av]);
      });

      return map;
    },
  });

  // Check hvilke listings der har fremtidig availability
  const listingsWithAvailability = useMemo(() => {
    if (!listingsQ.data || !availabilityQ.data) return new Set<string>();

    const now = new Date();
    const futureEnd = addDays(now, 365);
    const hasAvailability = new Set<string>();

    listingsQ.data.forEach(listing => {
      const avRows = availabilityQ.data.get(listing.id) || [];
      if (avRows.length === 0) return;

      const windows = expandAvailability(avRows as any, now, futureEnd);
      if (windows.length > 0) {
        hasAvailability.add(listing.id);
      }
    });

    return hasAvailability;
  }, [listingsQ.data, availabilityQ.data]);

  // Bookinger for dine pladser (join på listings.owner_id)
  const bookingsQ = useQuery({
    queryKey: ["ownerBookings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, listing_id, start_at, end_at, status, total_amount, stripe_payment_intent_id, listings!inner(id,title,address,cancellation_policy)"
        )
        .eq("listings.owner_id", user!.id)
        .order("start_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as OwnerBooking[];
    },
  });

  // Antal bookinger på dine pladser der afventer bekræftelse
  const pendingBookingsCount = useMemo(
    () => (bookingsQ.data ?? []).filter((b) => b.status === "pending").length,
    [bookingsQ.data]
  );

  // Bookinger hvor user er lejer
  const renterBookingsQ = useQuery({
    queryKey: ["renterBookings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, listing_id, start_at, end_at, status, total_amount, stripe_payment_intent_id, listings(id,title,address,cancellation_policy)"
        )
        .eq("renter_id", user!.id)
        .order("start_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as OwnerBooking[];
    },
  });

  // Reviews -> gennemsnitsrating (valgfrit/let)
  const reviewQ = useQuery({
    queryKey: ["ownerReviewsAvg", user?.id],
    enabled: !!user && !!bookingsQ.data && bookingsQ.data.length > 0,
    queryFn: async () => {
      const bookingIds = (bookingsQ.data ?? []).map((b) => b.id);
      if (!bookingIds.length) return { avg: 0, count: 0 };

      const { data, error } = await supabase
        .from("reviews")
        .select("rating, booking_id")
        .in("booking_id", bookingIds);
      if (error) throw error;

      const ratings = (data ?? []).map((r) => r.rating);
      const count = ratings.length;
      const avg = count ? ratings.reduce((a, b) => a + b, 0) / count : 0;
      return { avg, count };
    },
  });

  // Toggle synlighed
  const toggleVisibility = useMutation({
    mutationFn: async ({ id, visible }: { id: string; visible: boolean }) => {
      const { error } = await supabase.from("listings").update({ visibility: visible }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myListings", user?.id] });
    },
    onError: (e: any) => {
      toast({
        title: "Kunne ikke opdatere synlighed",
        description: e?.message ?? "Prøv igen.",
        variant: "destructive",
      });
    },
  });

  // Filter bookings by date (active vs past)
  const { activeOwnerBookings, pastOwnerBookings, activeRenterBookings, pastRenterBookings } = useMemo(() => {
    const now = new Date();
    const ownerBookings = bookingsQ.data ?? [];
    const renterBookings = renterBookingsQ.data ?? [];

    return {
      activeOwnerBookings: ownerBookings.filter((b) => new Date(b.end_at) >= now),
      pastOwnerBookings: ownerBookings.filter((b) => new Date(b.end_at) < now),
      activeRenterBookings: renterBookings.filter((b) => new Date(b.end_at) >= now),
      pastRenterBookings: renterBookings.filter((b) => new Date(b.end_at) < now),
    };
  }, [bookingsQ.data, renterBookingsQ.data]);

  // Stats - Platform fee er 15%, udlejer får 85%
  const PLATFORM_FEE = 0.15;
  const HOST_PERCENTAGE = 1 - PLATFORM_FEE; // 0.85 (85%)

  const stats = useMemo(() => {
    const listings = listingsQ.data ?? [];
    const bookings = bookingsQ.data ?? [];

    const activeListings = listings.filter((l) => l.visibility).length;
    const totalBookings = bookings.length;
    
    // Beregn indtjening efter 15% platform fee
    const totalEarningsOre = bookings
      .filter((b) => b.status === "confirmed" || b.status === "completed")
      .reduce((sum, b) => sum + (b.total_amount ?? 0) * HOST_PERCENTAGE, 0);
    const totalEarningsKr = (totalEarningsOre / 100).toFixed(0);

    const avgRating = reviewQ.data?.avg ?? 0;
    const avgRatingFmt = avgRating ? avgRating.toFixed(1) : "—";

    return { activeListings, totalBookings, totalEarningsKr, avgRatingFmt };
  }, [listingsQ.data, bookingsQ.data, reviewQ.data]);

  const pendingOwnerCount = activeOwnerBookings.filter((b) => b.status === "pending").length;
  const incompleteListingsCount = (listingsQ.data ?? []).filter((l) => l.visibility && !listingsWithAvailability.has(l.id)).length;
  const hasStripeReady = !!stripeConnectQ.data?.isFullyOnboarded && !!stripeConnectQ.data?.canReceivePayments;

  // Mutation til at bekræfte booking
  const confirmBooking = useMutation({
    mutationFn: async (bookingId: string) => {
      // Call secure edge function to confirm booking as listing owner (bypasses RLS safely)
      const { data, error } = await supabase.functions.invoke("confirm-booking", {
        body: { bookingId },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
    },
    onMutate: async (bookingId: string) => {
      setConfirmingId(bookingId);
      await qc.cancelQueries({ queryKey: ["ownerBookings", user?.id] });
      const previous = qc.getQueryData<OwnerBooking[]>(["ownerBookings", user?.id]);
      if (previous) {
        qc.setQueryData<OwnerBooking[]>(["ownerBookings", user?.id], previous.map(b => b.id === bookingId ? { ...b, status: "confirmed" } : b));
      }
      return { previous } as { previous?: OwnerBooking[] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ownerBookings", user?.id] });
      qc.refetchQueries({ queryKey: ["ownerBookings", user?.id] });
      toast({
        title: "Booking bekræftet",
        description: "Lejeren vil modtage en bekræftelse på email.",
      });
    },
    onError: (e: any, _bookingId, ctx: { previous?: OwnerBooking[] } | undefined) => {
      if (ctx?.previous) qc.setQueryData(["ownerBookings", user?.id], ctx.previous);
      toast({
        title: "Kunne ikke bekræfte booking",
        description: e?.message ?? "Prøv igen.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setConfirmingId(null);
    }
  });

  const isLoading =
    loading || listingsQ.isLoading || bookingsQ.isLoading || renterBookingsQ.isLoading || profileQ.isLoading || stripeConnectQ.isLoading;

  const formatKr = (ore: number | null | undefined) =>
    ore != null ? `${Math.round(ore / 100)} kr` : "—";

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Velkommen tilbage{profileQ.data?.name ? `, ${profileQ.data.name}` : ""}.
            </p>
          </div>

          <Link to="/create-listing" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Opret ny plads
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="flex items-center p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mr-4">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Din indtjening</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalEarningsKr} kr</p>
                <p className="text-xs text-muted-foreground">Efter 15% platform fee</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg mr-4">
                <House className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Aktive pladser</p>
                <p className="text-2xl font-bold text-foreground">{stats.activeListings}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg mr-4">
                <CalIcon className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Bookinger i alt</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalBookings}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg mr-4">
                <Star className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Gennemsnit rating</p>
                <p className="text-2xl font-bold text-foreground">{stats.avgRatingFmt}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Reviews Alert */}
        {user?.id && <PendingReviews currentUserId={user.id} />}

        <div className="mb-8 grid gap-3 md:grid-cols-3">
          <Card className={pendingOwnerCount ? "border-warning/40 bg-warning/5" : "bg-muted/30"}>
            <CardContent className="flex items-start gap-3 p-4">
              {pendingOwnerCount ? <AlertCircle className="mt-0.5 h-5 w-5 text-warning" /> : <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />}
              <div>
                <p className="font-medium">{pendingOwnerCount ? `${pendingOwnerCount} booking${pendingOwnerCount > 1 ? "er" : ""} afventer` : "Ingen afventende bookinger"}</p>
                <p className="text-sm text-muted-foreground">Bekræft hurtigt, så lejeren kan få adgang til pladsen.</p>
              </div>
            </CardContent>
          </Card>
          <Card className={incompleteListingsCount ? "border-warning/40 bg-warning/5" : "bg-muted/30"}>
            <CardContent className="flex items-start gap-3 p-4">
              {incompleteListingsCount ? <AlertCircle className="mt-0.5 h-5 w-5 text-warning" /> : <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />}
              <div>
                <p className="font-medium">{incompleteListingsCount ? `${incompleteListingsCount} plads${incompleteListingsCount > 1 ? "er" : ""} mangler tider` : "Alle synlige pladser har tider"}</p>
                <p className="text-sm text-muted-foreground">Pladser uden fremtidige tider kan ikke bookes effektivt.</p>
              </div>
            </CardContent>
          </Card>
          <Card className={hasStripeReady ? "bg-muted/30" : "border-warning/40 bg-warning/5"}>
            <CardContent className="flex items-start gap-3 p-4">
              {hasStripeReady ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" /> : <AlertCircle className="mt-0.5 h-5 w-5 text-warning" />}
              <div>
                <p className="font-medium">{hasStripeReady ? "Udbetalinger er klar" : "Gør udbetalinger klar"}</p>
                <p className="text-sm text-muted-foreground">
                  {hasStripeReady
                    ? "Dine pladser er synlige, og du får penge når nogen lejer."
                    : "Dine pladser er skjulte for lejere, indtil din udbetalingsopsætning er færdig."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stripe Connect Onboarding */}
        <ConnectOnboarding 
          userHasStripeAccount={!!stripeConnectQ.data?.hasStripeAccount}
          isFullyOnboarded={!!stripeConnectQ.data?.isFullyOnboarded}
          canReceivePayments={!!stripeConnectQ.data?.canReceivePayments}
          hasPhone={!!stripeConnectQ.data?.hasPhone}
          onOnboardingComplete={() => stripeConnectQ.refetch()}
        />

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <div className="w-full overflow-x-auto">
            <TabsList className="inline-flex w-auto min-w-full sm:grid sm:w-full sm:grid-cols-6">
              <TabsTrigger value="listings" className="whitespace-nowrap">Mine pladser</TabsTrigger>
              <TabsTrigger value="bookings" className="whitespace-nowrap relative">
                Bookinger
                {pendingBookingsCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold leading-none">
                    {pendingBookingsCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="messages" className="whitespace-nowrap">Beskeder</TabsTrigger>
              <TabsTrigger value="calendar" className="whitespace-nowrap">Kalender</TabsTrigger>
              <TabsTrigger value="earnings" className="whitespace-nowrap">Indtjening</TabsTrigger>
              <TabsTrigger value="profile" className="whitespace-nowrap">Profil</TabsTrigger>
            </TabsList>
          </div>

          {/* Mine pladser */}
          <TabsContent value="listings" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold">Mine parkeringspladser</h2>
              <Link to="/create-listing" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Tilføj ny plads
                </Button>
              </Link>
            </div>

            {isLoading ? (
              <div>Henter…</div>
            ) : (listingsQ.data?.length ?? 0) === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Du har ikke oprettet nogen pladser endnu.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {listingsQ.data!.map((l) => {
                  const photo = l.photos && l.photos.length ? l.photos[0] : placeholder;
                  const price = formatKr(l.base_price_day ?? null);
                  return (
                    <Card key={l.id}>
                      <div className="flex flex-col sm:flex-row sm:items-center p-4 sm:p-6 gap-4">
                        <img
                          src={photo}
                          alt={l.title}
                          className="w-full sm:w-16 h-32 sm:h-16 rounded-lg object-cover"
                        />
                        <div className="flex-1 space-y-1">
                          <h3 className="font-semibold text-foreground">{l.title}</h3>
                          <p className="text-sm text-muted-foreground">{l.address}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{price}/dag</span>
                          </div>
                        </div>

                          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                          {l.visibility ? (
                            listingsWithAvailability.has(l.id) ? (
                              <Badge className="bg-primary text-primary-foreground">Aktiv</Badge>
                            ) : (
                              <Badge variant="secondary">Ikke aktiv</Badge>
                            )
                          ) : (
                            <Badge variant="secondary">Skjult</Badge>
                          )}

                          {/* Show "Forny" button if no future availability */}
                          {!listingsWithAvailability.has(l.id) && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => setRenewDialog({ open: true, listingId: l.id, listingTitle: l.title })}
                              className="bg-success hover:bg-success/90"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Forny
                            </Button>
                          )}

                          {listingsWithAvailability.has(l.id) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditDialog({ open: true, listingId: l.id, listingTitle: l.title })}
                              title="Rediger tider"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              toggleVisibility.mutate({ id: l.id, visible: !l.visibility })
                            }
                            aria-label={l.visibility ? "Skjul" : "Vis"}
                          >
                            {l.visibility ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>

                          <Link to={`/spot/${l.id}`} className="w-full sm:w-auto">
                            <Button variant="outline" size="sm" className="w-full sm:w-auto">Se</Button>
                          </Link>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Bookinger */}
          <TabsContent value="bookings" className="space-y-6">
            {/* Mine udlejninger - Aktive */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Mine udlejninger</h2>
              <p className="text-sm text-muted-foreground">Aktive bookinger på dine parkeringspladser</p>
              {bookingsQ.isLoading ? (
                <div>Henter…</div>
              ) : activeOwnerBookings.length === 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground">Ingen aktive bookinger på dine pladser.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {activeOwnerBookings.map((b) => {
                    const start = format(new Date(b.start_at), "d. MMM yyyy", { locale: da });
                    const end = format(new Date(b.end_at), "d. MMM yyyy", { locale: da });
                    const amount = `${Math.round((b.total_amount ?? 0) / 100)} kr`;
                    const title = b.listings?.title ?? "(ukendt plads)";
                      return (
                        <Card key={b.id}>
                          <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                              <div className="flex-1 space-y-1">
                                <div className="font-semibold">{title}</div>
                                <div className="text-sm text-muted-foreground">
                                  {start} — {end}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-3">
                                <Badge variant={
                                  b.status === "confirmed" || b.status === "completed"
                                    ? "default"
                                    : b.status === "pending"
                                    ? "secondary"
                                    : "destructive"
                                }>
                                  {b.status}
                                </Badge>
                                <div className="font-semibold whitespace-nowrap">{amount}</div>
                                <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => navigate(`/spot/${b.listing_id}`)}>
                                  <MessageSquare className="h-4 w-4 mr-2" /> Vis plads
                                </Button>
                                {b.status === "pending" ? (
                                  <Button
                                    size="sm"
                                    onClick={() => confirmBooking.mutate(b.id)}
                                    disabled={confirmingId === b.id && confirmBooking.isPending}
                                    className="w-full sm:w-auto"
                                  >
                                    {confirmingId === b.id && confirmBooking.isPending ? "Bekræfter..." : "Bekræft"}
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" disabled className="w-full sm:w-auto">
                                    Bekræftet
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                  })}
                </div>
              )}
            </div>

            {/* Mine udlejninger - Tidligere */}
            {pastOwnerBookings.length > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Tidligere udlejninger</h3>
                  <div className="grid gap-4">
                    {pastOwnerBookings.map((b) => {
                      const start = format(new Date(b.start_at), "d. MMM yyyy", { locale: da });
                      const end = format(new Date(b.end_at), "d. MMM yyyy", { locale: da });
                      const amount = `${Math.round((b.total_amount ?? 0) / 100)} kr`;
                      const title = b.listings?.title ?? "(ukendt plads)";
                      return (
                        <Card key={b.id}>
                          <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                              <div className="flex-1 space-y-1">
                                <div className="font-semibold">{title}</div>
                                <div className="text-sm text-muted-foreground">
                                  {start} — {end}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-3">
                                <Badge variant={
                                  b.status === "confirmed" || b.status === "completed"
                                    ? "default"
                                    : "destructive"
                                }>
                                  {b.status}
                                </Badge>
                                <div className="font-semibold whitespace-nowrap">{amount}</div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Mine bookinger (som lejer) - Aktive */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Mine bookinger</h2>
              <p className="text-sm text-muted-foreground">Pladser du har booket</p>
              {renterBookingsQ.isLoading ? (
                <div>Henter…</div>
              ) : activeRenterBookings.length === 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground">Du har ingen aktive bookinger.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {activeRenterBookings.map((b) => {
                    const start = format(new Date(b.start_at), "d. MMM yyyy", { locale: da });
                    const end = format(new Date(b.end_at), "d. MMM yyyy", { locale: da });
                    const amount = `${Math.round((b.total_amount ?? 0) / 100)} kr`;
                    const title = b.listings?.title ?? "(ukendt plads)";
                    return (
                      <Card key={b.id}>
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex-1 space-y-1">
                              <div className="font-semibold">{title}</div>
                              <div className="text-sm text-muted-foreground">
                                {start} — {end}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <Badge variant={
                                b.status === "confirmed" || b.status === "completed"
                                  ? "default"
                                  : b.status === "pending"
                                  ? "secondary"
                                  : "destructive"
                              }>
                                {b.status}
                              </Badge>
                              <div className="font-semibold whitespace-nowrap">{amount}</div>
                              <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => navigate(`/spot/${b.listing_id}`)}>
                                <MessageSquare className="h-4 w-4 mr-2" /> Kontakt/vis
                              </Button>
                              <CancelBookingDialog
                                booking={{
                                  id: b.id,
                                  start_at: b.start_at,
                                  end_at: b.end_at,
                                  total_amount: b.total_amount,
                                  status: b.status,
                                  stripe_payment_intent_id: b.stripe_payment_intent_id,
                                  listings: b.listings,
                                }}
                                onCancelComplete={() => {
                                  qc.invalidateQueries({ queryKey: ["renterBookings", user?.id] });
                                }}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mine bookinger - Tidligere */}
            {pastRenterBookings.length > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Tidligere bookinger</h3>
                  <div className="grid gap-4">
                    {pastRenterBookings.map((b) => {
                      const start = format(new Date(b.start_at), "d. MMM yyyy", { locale: da });
                      const end = format(new Date(b.end_at), "d. MMM yyyy", { locale: da });
                      const amount = `${Math.round((b.total_amount ?? 0) / 100)} kr`;
                      const title = b.listings?.title ?? "(ukendt plads)";
                      return (
                        <PastBookingCard 
                          key={b.id} 
                          booking={b} 
                          start={start} 
                          end={end} 
                          amount={amount} 
                          title={title}
                          currentUserId={user?.id}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages" className="space-y-4">
            {user?.id && <ConversationsList currentUserId={user.id} />}
          </TabsContent>

          {/* Profil */}
          <TabsContent value="profile" className="space-y-4">
            <h2 className="text-xl font-semibold">Min profil</h2>
            <Card>
              <CardHeader>
                <CardTitle>Profiloplysninger</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar Upload */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Profilbillede</label>
                  {user?.id && (
                    <AvatarUpload
                      userId={user.id}
                      currentPhotoUrl={profileQ.data?.photo_url}
                      userName={profileQ.data?.name}
                      onUploadComplete={() => profileQ.refetch()}
                      size="lg"
                    />
                  )}
                  <p className="text-sm text-muted-foreground mt-2">
                    Upload et profilbillede (maks 5MB)
                  </p>
                </div>

                <Separator />

                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Navn</label>
                    <p className="text-foreground">{profileQ.data?.name || "Ikke angivet"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <p className="text-foreground">{profileQ.data?.email || "Ikke angivet"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Telefon</label>
                    <p className="text-foreground">{profileQ.data?.phone || "Ikke angivet"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Kalender */}
          <TabsContent value="calendar" className="space-y-4">
            <h2 className="text-xl font-semibold">Kalender</h2>
            {user?.id && <BookingsCalendar userId={user.id} />}
          </TabsContent>

          {/* Indtjening */}
          <TabsContent value="earnings" className="space-y-6">
            <h2 className="text-xl font-semibold">Indtjening</h2>
            
            {/* Stripe Balance Widget */}
            {user?.id && <StripeBalanceWidget userId={user.id} />}
            
            {/* Earnings Overview */}
            {user?.id && <EarningsOverview userId={user.id} />}
          </TabsContent>
        </Tabs>
      </div>

      {/* Renew Availability Dialog */}
      <RenewAvailabilityDialog
        open={renewDialog.open}
        onOpenChange={(open) => setRenewDialog({ ...renewDialog, open })}
        listingId={renewDialog.listingId}
        listingTitle={renewDialog.listingTitle}
      />

      {/* Edit Availability Dialog */}
      <EditAvailabilityDialog
        open={editDialog.open}
        onOpenChange={(open) => setEditDialog({ ...editDialog, open })}
        listingId={editDialog.listingId}
        listingTitle={editDialog.listingTitle}
      />

      <Footer />
    </div>
  );
}
